import React, { memo, useEffect, useRef, useState } from 'react';
import { Col, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';

import { checkEmail, checkPhone, showMsg } from '../../services/library';
import CustomModal from '../../components/GeneralForm/CustomModal';
import CustomCard from '../../components/GeneralForm/CustomCard';
import CustomSelect from '../../components/GeneralForm/CustomSelect';
import CommonService from '../../services/commonService';

const NeedTrainingComponent = props => {
  const { activeDealersList, user } = props;

  const { user_email, user_displayname, user_phone } = user;

  const TRAINING_OPTIONS = [
    { label: 'Full Process', value: 'full_process' },
    { label: 'Photos', value: 'photos' },
    { label: 'Videos', value: 'videos' },
    { label: '360s', value: '360s' },
    { label: 'Window Stickers', value: 'window_stickers' },
    { label: 'Other - See Comments', value: 'other' }
  ];

  const [errors, setErrors] = useState({});
  const [spanishTrainer, setSpanishTrainer] = useState(0);

  const [formData, setFormData] = useState({
    name: user_displayname || '',
    dealership: null,
    phone: user_phone || '',
    email: user_email || '',
    trainingTypes: [],
    spanish_trainer: spanishTrainer,
    comments: ''
  });
  const commentsRef = useRef(null);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      spanish_trainer: spanishTrainer,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleCheckboxChange = e => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const updatedTypes = checked
        ? [...new Set([...prev.trainingTypes, value])]
        : prev.trainingTypes.filter(item => item !== value);
      return {
        ...prev,
        trainingTypes: updatedTypes
      };
    });

    if (checked) {
      setErrors(prev => ({
        ...prev,
        trainingTypes: ''
      }));
    }

    if (value === 'other' && checked) {
      commentsRef.current?.focus();
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.dealership) {
      newErrors.dealership = 'Dealership is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!checkPhone(formData.phone.trim())) {
      newErrors.phone = 'Invalid phone number format';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!checkEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.trainingTypes || formData.trainingTypes.length === 0) {
      newErrors.trainingTypes = 'Please select at least one training type';
    } else if (
      formData.trainingTypes.length === 1 &&
      formData.trainingTypes[0] === 'other' &&
      !(formData.comments || '').trim()
    ) {
      newErrors.comments = 'Please describe the training in comments';
    }

    return newErrors;
  };

  const handleDealerChange = item => {
    setFormData(prev => ({ ...prev, dealership: item }));
  };

  const resetForm = () => {
    setFormData({
      name: user_displayname || '',
      dealership: props.dealer || null,
      phone: user_phone || '',
      email: user_email || '',
      trainingTypes: [],
      comments: ''
    });
    setSpanishTrainer(0);
    setErrors({});
  };

  const handleSubmit = async () => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const isOnlyOther = formData.trainingTypes.length === 1 && formData.trainingTypes[0] === 'other';
    const selectedTrainingLabels = TRAINING_OPTIONS.filter(
      opt => (formData.trainingTypes || []).includes(opt.value) && opt.value !== 'other'
    ).map(opt => opt.label);

    const payload = {
      dealer_id: formData.dealership?.value || null,
      user_name: formData.name.trim() || '',
      user_phone: formData.phone.trim() || '',
      user_email: formData.email.trim() || '',
      spanish_trainer: spanishTrainer,
      training_types: isOnlyOther ? ['Other'] : selectedTrainingLabels,
      comments: formData.comments.trim() || ''
    };

    props.handleFullLayoutLoader(true);

    try {
      const response = await CommonService.sendTrainingRequest(payload);

      if (response.status) {
        showMsg(response.message, 'success');
        resetForm();
        props.toggle();
      } else {
        showMsg(response.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    } finally {
      props.handleFullLayoutLoader(false);
    }
  };

  useEffect(() => {
    if (props.dealer) {
      setFormData(prev => ({
        ...prev,
        dealership: props.dealer
      }));
    }
  }, [props.dealer]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      closeClick={props.toggle}
      modalScrollable={true}
      modalSize="lg"
      saveClick={handleSubmit}
      submitButtonName="Submit Request"
      modalTitle={
        <>
          <span>Need Training</span>
          <i className="fa fa-graduation-cap ml-2 text-info fa-xl" aria-hidden="true" />
        </>
      }
      modalBackDrop="static"
    >
      <span className="text-bold text-danger">
        Request additional training directly from our Training Team
      </span>
      <CustomCard addedClass="mt-2">
        <FormGroup row>
          <Label htmlFor="name" sm={4} className="text-left">
            <span className="text-danger">*</span> Name
          </Label>
          <Col sm={8}>
            <Input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              invalid={!!errors.name}
              placeholder="Enter Name"
              className="b-0 bb-1"
            />
            <FormFeedback>{errors.name}</FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label htmlFor="dealership" sm={4} className="text-left">
            <span className="text-danger">*</span> Dealership
          </Label>
          <Col sm={8}>
            <CustomSelect
              options={activeDealersList}
              selected={formData.dealership}
              onChange={handleDealerChange}
              placeholder="Select Dealership"
              className="team-select b-0 bb-1"
            />
            {errors.dealership && (
              <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>
                {errors.dealership}
              </div>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label htmlFor="phone" sm={4} className="text-left">
            <span className="text-danger">*</span> Phone Number
          </Label>
          <Col sm={8}>
            <Input
              type="text"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              invalid={!!errors.phone}
              placeholder="Enter Phone Number"
              className="b-0 bb-1"
            />
            <FormFeedback>{errors.phone}</FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label htmlFor="email" sm={4} className="text-left">
            <span className="text-danger">*</span> Email
          </Label>
          <Col sm={8}>
            <Input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              invalid={!!errors.email}
              placeholder="Enter Email"
              className="b-0 bb-1"
            />
            <FormFeedback>{errors.email}</FormFeedback>
          </Col>
        </FormGroup>
      </CustomCard>

      {/* checkbox section */}
      <CustomCard title="Need training on ">
        <div className="px-2">
          <Row>
            {TRAINING_OPTIONS.map(item => (
              <Col xs={12} sm={6} md={4} key={item.value} className="mb-2">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      value={item.value}
                      checked={formData.trainingTypes.includes(item.value)}
                      onChange={handleCheckboxChange}
                    />
                    {item.label}
                  </Label>
                </FormGroup>
              </Col>
            ))}
          </Row>

          {errors.trainingTypes && (
            <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>
              {errors.trainingTypes}
            </div>
          )}
        </div>
      </CustomCard>

      <CustomCard>
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              id="spanish_trainer"
              name="spanish_trainer"
              checked={spanishTrainer === 1}
              onChange={e => setSpanishTrainer(+e.currentTarget.checked)}
            />
            Spanish Speaking Trainer
          </Label>
        </FormGroup>
      </CustomCard>

      {/* comment section */}
      <CustomCard title="Comments">
        <FormGroup>
          <Input
            type="textarea"
            name="comments"
            id="comments"
            rows="4"
            invalid={!!errors.comments}
            placeholder="Add any additional details or requests..."
            value={formData.comments}
            onChange={handleChange}
            innerRef={commentsRef}
            maxLength={300}
          />
          <div className="text-right mt-1" style={{ fontSize: '0.8rem', color: '#888' }}>
            {(formData.comments || '').length} / 300 characters
          </div>

          <FormFeedback>{errors.comments}</FormFeedback>
        </FormGroup>
      </CustomCard>
    </CustomModal>
  );
};

NeedTrainingComponent.displayName = 'NeedTrainingComponent';
export default memo(NeedTrainingComponent);
